import { Checkbox } from '@creditornot/cb-checkbox';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { InputAlert } from '@creditornot/cb-input';
import { InputSwitch } from '@creditornot/cb-switch';
import React, { useContext, useMemo } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ProductHierarchyTag } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import { isDefined } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';

import { FormValues, AvailabilityDetails } from './types';

type RestrictedItemFieldsProps = {
  productHierarchyOptions: {
    value: string;
    label: string;
    secondaryContent: string;
    parentId: string | null;
    childrenIds: string[];
    tags: ProductHierarchyTag[];
  }[];
};

const StyledInputContainer = styled(InputContainer)`
  max-width: 392px;
`;

const StyledInputAlert = styled(InputAlert)`
  margin-block-start: 8px;
`;

const RestrictedItemFields = ({ productHierarchyOptions }: RestrictedItemFieldsProps) => {
  const menuFeatures = useContext(MenuFeaturesContext);
  const menuPermissions = useContext(MenuPermissionsContext);
  const { alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();
  const intl = useIntl();
  const { values } = useFormState<FormValues>();
  const form = useForm<FormValues>();

  const isTobaccoProductHierarchyLevel = useMemo(
    () =>
      !!values.productHierarchyLevelId &&
      !!productHierarchyOptions
        .find(option => option.value === values.productHierarchyLevelId)
        ?.tags.includes('tobacco'),
    [productHierarchyOptions, values.productHierarchyLevelId],
  );

  const overTheCounterEnabled = useMemo(
    () =>
      menuFeatures.overTheCounter &&
      values.productHierarchyLevelId &&
      !!productHierarchyOptions
        .find(option => option.value === values.productHierarchyLevelId)
        ?.tags.includes('over_the_counter'),
    [menuFeatures.overTheCounter, productHierarchyOptions, values.productHierarchyLevelId],
  );

  const isAlcoholPercentageExceedWarningLimit = values.alcoholPercentage > alcoholWarningPermilleLimit;

  const hasItemRestrictedProperty = isAlcoholPercentageExceedWarningLimit || isTobaccoProductHierarchyLevel;

  const restrictionsInfo = useMemo(
    () =>
      [
        values.isOTCRestricted || values.isDeliveryHandshakeRestricted
          ? intl.formatMessage({ id: 'menu-item.no-contact-delivery-disabled' })
          : undefined,
        values.isAgeRestricted ? intl.formatMessage({ id: 'menu-item.proof-of-age-required' }) : undefined,
      ].filter(isDefined),
    [intl, values.isAgeRestricted, values.isOTCRestricted, values.isDeliveryHandshakeRestricted],
  );

  return (
    <>
      <FormRow
        label={intl.formatMessage({ id: 'menu-item.restricted-item' })}
        info={intl.formatMessage({ id: 'menu-item.restricted-item-info' })}
      >
        <Field<AvailabilityDetails['isRestrictedItem']> name="isRestrictedItem">
          {({ input }) => (
            <InputSwitch
              checked={hasItemRestrictedProperty ? true : input.value}
              disabled={hasItemRestrictedProperty || !menuPermissions.contentEdit}
              onChange={() => {
                const newValue = !input.value;
                input.onChange(newValue);
                form.batch(() => {
                  (['isOTCRestricted', 'isAgeRestricted', 'isDeliveryHandshakeRestricted'] as const).forEach(field => {
                    if (!newValue && form.getFieldState(field)?.value) {
                      form.change(field, false);
                    }
                  });

                  if (!newValue && form.getFieldState('ageLimit')?.value) {
                    form.change('ageLimit', undefined);
                  }
                });
              }}
            />
          )}
        </Field>
      </FormRow>

      {values.isRestrictedItem && (
        <FormRow
          label={intl.formatMessage({
            id: 'menu-item.restriction-type',
          })}
        >
          <Field<boolean> name="isOTCRestricted" type="checkbox">
            {field => (
              <Checkbox checked={field.input.checked} onChange={field.input.onChange} disabled={!overTheCounterEnabled}>
                <FormattedMessage id="menu-item.over-the-counter-medicine" />
              </Checkbox>
            )}
          </Field>
          <Field<boolean> name="isAgeRestricted" type="checkbox">
            {field => (
              <Checkbox
                style={{ marginBlockStart: 8 }}
                checked={hasItemRestrictedProperty ? true : field.input.checked}
                onChange={field.input.onChange}
                disabled={hasItemRestrictedProperty}
              >
                <FormattedMessage id="menu-item.age-restricted-item" />
              </Checkbox>
            )}
          </Field>
          {values.isAgeRestricted && (
            <Field<AvailabilityDetails['ageLimit']> name="ageLimit">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => (
                    <StyledInputContainer
                      style={{ marginBlockStart: 8 }}
                      label={intl.formatMessage({ id: 'menu-item.specify-age-limit' })}
                    >
                      <StyledInputContainer>
                        <InputNumber {...input} decimals={0} />
                      </StyledInputContainer>
                    </StyledInputContainer>
                  )}
                </RFFFieldErrorWrapper>
              )}
            </Field>
          )}
          <Field<boolean> name="isDeliveryHandshakeRestricted" type="checkbox">
            {field => (
              <Checkbox style={{ marginBlockStart: 8 }} checked={field.input.checked} onChange={field.input.onChange}>
                <FormattedMessage id="menu-item.delivery-handshake" />
              </Checkbox>
            )}
          </Field>
          {restrictionsInfo.length > 0 && (
            <StyledInputAlert variant="info">{restrictionsInfo.join(' ')}</StyledInputAlert>
          )}
        </FormRow>
      )}
    </>
  );
};

export default RestrictedItemFields;
